import { Link } from 'react-router-dom';

import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { ROUTES } from '@/shared/lib/constants/routes';
import { highlightText } from '@/shared/lib/text';
import { Chip } from '@/shared/ui/Chip';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { Switch } from '@/shared/ui/Switch';
import { Tooltip } from '@/shared/ui/Tooltip';

import { type DagListRunStatus, formatDagMaturity } from '@/entities/dag';

import type { DagDashboardSortField, DagDashboardSortOrder, DagTableRow } from '@/features/dag-list/model/types';
import { DagSelectCell } from '@/features/dag-list/ui/DagSelectCell';
import { DagStatusBadge } from '@/features/dag-list/ui/DagStatusBadge';

import styles from './dagTableColumns.module.css';

const formatDateTime = (value?: string | null) => {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';
};

const toAntdSortOrder = (
  columnKey: DagDashboardSortField,
  sortBy?: DagDashboardSortField,
  sortOrder?: DagDashboardSortOrder
): 'ascend' | 'descend' | undefined => {
  if (sortBy !== columnKey) return undefined;
  return sortOrder === 'asc' ? 'ascend' : 'descend';
};

const renderMaturity = (maturity?: string) => {
  return maturity ? formatDagMaturity(maturity) : <span className={styles.secondaryText}>-</span>;
};

type GetDagTableColumnsParams = {
  activationLoadingDagUuid?: string;
  deletingDagUuid?: string;
  triggeringDagUuid?: string;
  bookmarkingDagUuid?: string;
  onDagDetailNavigate: () => void;
  onFavoriteToggle: (dagUuid: string) => void;
  onActivationClick: (dagUuid: string, nextIsSchedulingEnabled: boolean) => void;
  onTriggerClick: (record: DagTableRow) => void;
  onDeleteClick: (dagUuid: string, name?: string) => void;
  onVersionChange: (dagUuid: string, versionLabel: string) => void;
  sortBy?: DagDashboardSortField;
  sortOrder?: DagDashboardSortOrder;
  searchQuery?: string;
};

export const getDagTableColumns = ({
  activationLoadingDagUuid,
  deletingDagUuid,
  triggeringDagUuid,
  bookmarkingDagUuid,
  onDagDetailNavigate,
  onFavoriteToggle,
  onActivationClick,
  onTriggerClick,
  onDeleteClick,
  onVersionChange,
  sortBy,
  sortOrder,
  searchQuery,
}: GetDagTableColumnsParams): ColumnsType<DagTableRow> => {
  const trimmedSearchQuery = searchQuery?.trim() ?? '';

  return [
    {
      title: 'Start/Stop',
      dataIndex: 'isSchedulingEnabled',
      key: 'isSchedulingEnabled',
      width: 100,
      ellipsis: true,
      align: 'center',
      render: (isSchedulingEnabled: boolean, record) => {
        const canToggleScheduling =
          record.hasDeployment &&
          record.deployedVersion &&
          record.selectedVersion === record.deployedVersion &&
          record.displayStatus === 'success';
        const isActivationLoading = activationLoadingDagUuid === record.dagUuid;

        return (
          <div className={styles.switchWrapper}>
            <Switch
              checked={isSchedulingEnabled}
              disabled={!canToggleScheduling || isActivationLoading}
              onChange={(checked) => onActivationClick(record.dagUuid, checked)}
              aria-label={`${record.dagUuid} 활성 상태`}
              color="standard"
            />
          </div>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 198,
      ellipsis: true,
      sorter: true,
      sortOrder: toAntdSortOrder('name', sortBy, sortOrder),
      render: (name: string, record) => {
        const highlightedName = trimmedSearchQuery
          ? highlightText(name, trimmedSearchQuery, { className: 'highlight' })
          : name;

        return (
          <Link to={`${ROUTES.DAG_DETAIL}/${record.dagUuid}`} className={styles.idLink} onClick={onDagDetailNavigate}>
            <EllipsisText text={name} className={styles.idText} tooltipPlacement="topLeft">
              {highlightedName}
            </EllipsisText>
          </Link>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 198,
      render: (description?: string | null) => {
        const trimDescription = description?.trim();

        if (!trimDescription) return '-';

        const highlightedDescription = trimmedSearchQuery
          ? highlightText(trimDescription, trimmedSearchQuery, { className: 'highlight' })
          : trimDescription;

        return (
          <EllipsisText
            text={trimDescription}
            className={styles.descriptionText}
            tooltipPlacement="bottomRight"
            arrow={false}>
            {highlightedDescription}
          </EllipsisText>
        );
      },
    },
    {
      title: 'Ver.',
      dataIndex: 'selectedVersion',
      key: 'version',
      width: 198,
      render: (_selectedVersion: number | undefined, record) => {
        const latestVersionLabel = record.latestVersion ? `${record.latestVersion}` : undefined;
        const value = record.selectedVersion != null ? `${record.selectedVersion}` : undefined;

        if (value === 'initialized') {
          return (
            <div className={styles.versionCell}>
              <div className={styles.initialized}>Initialized</div>
            </div>
          );
        }

        return (
          <div className={styles.versionCell}>
            <DagSelectCell
              dagId={record.dagUuid}
              latestVersionLabel={latestVersionLabel}
              value={value}
              versionSnapshots={record.versionSnapshots}
              onChange={(dagUuid, versionLabel) => onVersionChange(dagUuid, versionLabel)}
            />
          </div>
        );
      },
    },
    {
      title: 'Schedule',
      dataIndex: 'displaySchedule',
      key: 'schedule',
      width: 198,
      render: (schedule: string | undefined | null, record) => {
        if (record.deployedVersion !== record.selectedVersion) return '-';
        return <span>{schedule ?? '-'}</span>;
      },
    },
    {
      title: 'Next Execution',
      dataIndex: 'displayNextRun',
      key: 'nextRun',
      width: 180,
      sorter: true,
      sortOrder: toAntdSortOrder('next_run', sortBy, sortOrder),
      render: (nextRun: string | null | undefined) => {
        return formatDateTime(nextRun);
      },
    },
    {
      title: 'Last Execution',
      dataIndex: 'displayLastRun',
      key: 'lastRun',
      width: 180,
      sorter: true,
      sortOrder: toAntdSortOrder('last_run', sortBy, sortOrder),
      render: (lastRun?: string | null) => {
        return formatDateTime(lastRun);
      },
    },
    {
      title: 'Status',
      dataIndex: 'displayStatus',
      key: 'status',
      width: 120,
      render: (status?: DagListRunStatus) => {
        return <DagStatusBadge runStatus={status} />;
      },
    },
    {
      title: 'Tag',
      dataIndex: 'tags',
      key: 'tags',
      width: 180,
      onCell: () => ({ className: styles.tagCell }),
      render: (tags?: string[]) => {
        if (!tags || tags.length === 0) {
          return <span className={styles.tagEmpty}>-</span>;
        }

        const items = tags.map((tag) => ({ tagFQN: tag, name: tag, displayName: tag }));
        const firstChip = items[0];
        const restChips = items.slice(1);

        return (
          <div className={styles.tagContainer}>
            <Chip
              className={styles.tagItem}
              icon={<Icon name="tag" className={styles.tagIcon} size={12} />}
              label={firstChip.displayName}
            />
            {restChips.length > 0 && (
              <Tooltip
                title={restChips.map((chip) => chip.displayName).join(', ')}
                rootClassName={styles.whiteTooltip}
                placement="bottomLeft">
                <div className={styles.restChipNumber}>+{restChips.length}</div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Maturity',
      dataIndex: 'maturity',
      key: 'maturity',
      width: 120,
      render: (maturity?: string) => {
        return renderMaturity(maturity);
      },
    },
    {
      title: 'Tools',
      key: 'tools',
      width: 120,
      align: 'left',
      render: (_value: unknown, record) => {
        const isFavorite = record.isBookmarked;

        return (
          <div className={styles.toolsCell}>
            <IconButton
              className={styles.iconButton}
              onClick={() => onTriggerClick(record)}
              disabled={triggeringDagUuid === record.dagUuid}
              tooltip="트리거"
              ariaLabel={`${record.dagUuid} 트리거`}>
              <Icon name="run" size={16} />
            </IconButton>
            <IconButton
              className={styles.iconButton}
              onClick={() => onFavoriteToggle(record.dagUuid)}
              disabled={bookmarkingDagUuid === record.dagUuid}
              tooltip={isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
              ariaLabel={`즐겨찾기 ${isFavorite ? '해제' : '설정'}`}>
              <Icon name={isFavorite ? 'star-filled' : 'star'} size={16} />
            </IconButton>
            <IconButton
              className={styles.iconButton}
              onClick={() => onDeleteClick(record.dagUuid, record.name)}
              disabled={deletingDagUuid === record.dagUuid}
              tooltip="삭제"
              ariaLabel={`${record.dagUuid} 삭제`}>
              <Icon name="trash" size={16} />
            </IconButton>
          </div>
        );
      },
    },
  ];
};
